// Pure helpers of the dashboard runtime and builder: no React, no fetches.
import type { DashboardDoc, DashboardPage, FlowPeriod, LayoutItem, QuerySpec, Threshold, Widget, WidgetSeries, WidgetType } from '@twinlink/shared';
import { orgUnits } from '@web/lib/units';

/** Widget types the runtime can draw today; the others show a notice. */
export const SUPPORTED_WIDGETS = ['valueCard', 'trend', 'stateTimeline', 'xy', 'bar', 'gauge', 'statusLamp', 'liveTable', 'periodTable', 'seqLog', 'wellsTable', 'schematic', 'map', 'image', 'text', 'link'] as const satisfies readonly WidgetType[];
export const isSupported = (type: WidgetType) => (SUPPORTED_WIDGETS as readonly string[]).includes(type);
/** Widgets that load their own data (one row per well): their series are not the dashboard's. */
export const loadsOwnData = (type: WidgetType) => type === 'wellsTable';

/** How many signals a widget type takes (0 = none). */
export const SERIES_LIMIT: Record<WidgetType, number> = { valueCard: 1, gauge: 1, statusLamp: 1, trend: 12, liveTable: 24, bar: 12, xy: 2, periodTable: 12, seqLog: 0, text: 0, map: 0, image: 0, schematic: 0, wellsTable: 12, link: 0, stateTimeline: 12 };
export const DEFAULT_SIZE: Record<WidgetType, { w: number; h: number }> = {
  valueCard: { w: 2, h: 3 },
  gauge: { w: 3, h: 6 },
  statusLamp: { w: 2, h: 3 },
  trend: { w: 6, h: 8 },
  liveTable: { w: 4, h: 8 },
  seqLog: { w: 4, h: 8 },
  text: { w: 4, h: 3 },
  bar: { w: 4, h: 6 },
  xy: { w: 4, h: 6 },
  periodTable: { w: 6, h: 6 },
  map: { w: 4, h: 6 },
  image: { w: 3, h: 4 },
  schematic: { w: 8, h: 10 },
  wellsTable: { w: 12, h: 7 },
  link: { w: 2, h: 2 },
  stateTimeline: { w: 12, h: 5 },
};

/** `widgetId.refId`: the key of one series everywhere (resolve, query, results). */
export const seriesKey = (widgetId: string, refId: string) => `${widgetId}.${refId}`;

export interface SeriesRef {
  key: string;
  widgetId: string;
  widget: Widget;
  series: WidgetSeries;
}
export function seriesRefs(page: DashboardPage): SeriesRef[] {
  return Object.entries(page.widgets).flatMap(([widgetId, widget]) =>
    isSupported(widget.type) && !loadsOwnData(widget.type) ? widget.config.series.map((series) => ({ key: seriesKey(widgetId, series.refId), widgetId, widget, series })) : [],
  );
}

/** What a query result is for: the widget's own data, or one statistic column of a table. */
export type Slot = 'main' | 'min' | 'max' | 'mean';
export const slotOf = (queryKey: string) => queryKey.split('~')[0] as Slot;

/**
 * Groups every series of the page into as few queries as possible: series that share a time
 * range, an aggregation and a resolution travel in one QuerySpec, and all specs go out in one
 * batch request.
 */
export function buildQueries(page: DashboardPage, resolved: Record<string, string | null>, range: { from: string; to: string }, timeZone?: string): Array<{ key: string; spec: QuerySpec }> {
  const groups = new Map<string, { spec: QuerySpec; slot: Slot }>();
  const add = (ref: SeriesRef, agg: NonNullable<WidgetSeries['agg']>, maxPoints: number, slot: Slot = 'main', interval: QuerySpec['interval'] = 'auto') => {
    const signalId = resolved[ref.key];
    if (!signalId) return;
    const from = ref.widget.config.timeFrom ?? range.from;
    const to = ref.widget.config.timeFrom ? 'now' : range.to;
    let base = `${slot}|${from}|${to}|${agg}|${maxPoints}|${interval}`;
    // A QuerySpec takes at most 60 series.
    let part = 0;
    while ((groups.get(`${base}#${part}`)?.spec.series.length ?? 0) >= 60) part++;
    base = `${base}#${part}`;
    const g = groups.get(base) ?? { spec: { range: { from, to }, timeZone, maxPoints, interval, fill: 'none' as const, series: [] }, slot };
    g.spec.series.push({ refId: ref.key, signalId, agg, displayUnitId: ref.series.displayUnitId ?? undefined });
    groups.set(base, g);
  };
  for (const ref of seriesRefs(page)) {
    switch (ref.widget.type) {
      case 'trend':
        add(ref, ref.series.agg ?? 'mean', 800);
        break;
      case 'stateTimeline':
        // A state code must not be averaged (the mean of 12 and 13 is no state): the last value of each bucket.
        add(ref, ref.series.agg ?? 'last', 1000);
        break;
      case 'valueCard':
        // The sparkline and, on a historical range, the value at the end of the range.
        add(ref, ref.series.agg && ref.series.agg !== 'raw' ? ref.series.agg : 'last', 120);
        break;
      case 'gauge':
      case 'statusLamp':
        add(ref, 'last', 120);
        break;
      case 'liveTable':
        add(ref, 'last', 120);
        // True extremes need their own aggregation: the min of per-bucket minimums is exact.
        for (const col of ref.widget.config.columns ?? []) add(ref, col, 60, col);
        break;
      case 'periodTable':
      case 'bar':
        // One row or bar per hour or per day: the server aggregates, the widget only prints.
        // Flow periods have no fixed length: fine buckets are reduced per period in the browser.
        if (ref.widget.config.periods === 'flowPeriods') add(ref, ref.series.agg ?? 'mean', 1000);
        else add(ref, ref.series.agg ?? 'mean', 1000, 'main', ref.widget.config.periods === 'daily' ? '1d' : '1h');
        break;
      case 'xy':
        // Both signals travel in the same query, so their buckets line up.
        add(ref, 'mean', 500);
        break;
      default:
        break;
    }
  }
  return [...groups.values()].slice(0, 40).map((g, i) => ({ key: `${g.slot}~${i}`, spec: g.spec }));
}

export type Points = Array<[number, number | null]>;

export function lastPoint(points: Points | undefined): [number, number] | null {
  if (!points) return null;
  for (let i = points.length - 1; i >= 0; i--) {
    const p = points[i]!;
    if (p[1] !== null) return [p[0], p[1]];
  }
  return null;
}

export function reduceStat(points: Points | undefined, stat: 'min' | 'max' | 'mean'): number | null {
  if (!points) return null;
  let acc: number | null = null;
  let sum = 0;
  let n = 0;
  for (const [, v] of points) {
    if (v === null) continue;
    if (stat === 'mean') {
      sum += v;
      n++;
    } else if (acc === null || (stat === 'min' ? v < acc : v > acc)) acc = v;
  }
  return stat === 'mean' ? (n ? sum / n : null) : acc;
}

export interface PeriodValue {
  period: FlowPeriod;
  value: number | null;
  /** The period starts before or ends after the data that was loaded. */
  partial: boolean;
}

/**
 * One value per flow period from evenly spaced buckets: buckets have the same length, so the
 * mean of bucket means is the time-weighted mean of the period. Periods outside the loaded
 * window are left out; a period that overlaps its edge is marked partial.
 */
export function reduceByPeriods(points: Points, periods: FlowPeriod[], agg: NonNullable<WidgetSeries['agg']>, window: { from: number; to: number }): PeriodValue[] {
  const out: PeriodValue[] = [];
  for (const period of periods) {
    const start = Date.parse(period.startAt);
    const end = period.endAt ? Date.parse(period.endAt) : window.to;
    if (!(end > window.from) || !(start < window.to)) continue;
    let acc: number | null = null;
    let sum = 0;
    let n = 0;
    for (const [t, v] of points) {
      if (v === null || t < start || t >= end) continue;
      if (agg === 'min') acc = acc === null || v < acc ? v : acc;
      else if (agg === 'max') acc = acc === null || v > acc ? v : acc;
      else if (agg === 'last' || agg === 'raw') acc = v;
      else if (agg === 'first') acc = acc ?? v;
      else {
        sum += v;
        n++;
      }
    }
    const value = agg === 'sum' ? (n ? sum : null) : agg === 'count' ? n : agg === 'spread' ? null : n > 0 ? sum / n : acc;
    out.push({ period, value, partial: start < window.from || end > window.to });
  }
  return out;
}

/** Pairs two series bucket by bucket: `[x, y, time]` where both have a value. */
export function pairXY(x: Points, y: Points): Array<[number, number, number]> {
  const ys = new Map(y);
  const out: Array<[number, number, number]> = [];
  for (const [t, xv] of x) {
    const yv = ys.get(t);
    if (xv !== null && yv !== null && yv !== undefined) out.push([xv, yv, t]);
  }
  return out;
}

const compare = (op: Threshold['op'], value: number, limit: number) => (op === '>' ? value > limit : op === '>=' ? value >= limit : op === '<' ? value < limit : value <= limit);

/** The colour of the last threshold the value crosses (later rules win), or null. */
export function thresholdColor(value: number | null, thresholds: Threshold[], displayUnitId: string | null): Threshold['color'] | null {
  if (value === null) return null;
  let color: Threshold['color'] | null = null;
  for (const th of thresholds) {
    let limit = th.value;
    if (th.unitId && displayUnitId && th.unitId !== displayUnitId) {
      try {
        limit = orgUnits.convert(th.value, th.unitId, displayUnitId);
      } catch {
        continue;
      }
    }
    if (compare(th.op, value, limit)) color = th.color;
  }
  return color;
}

// ---- Builder ----
export function newId(prefix: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  let n = used.size + 1;
  while (used.has(`${prefix}${n}`)) n++;
  return `${prefix}${n}`;
}

export function newWidget(type: WidgetType): Widget {
  return { type, title: '', config: { series: [], thresholds: [], ...(type === 'valueCard' ? { sparkline: true } : {}), ...(type === 'trend' ? { legend: true, annotations: true } : {}), ...(type === 'gauge' ? { min: 0, max: 100 } : {}), ...(type === 'text' ? { markdown: '' } : {}), ...(type === 'periodTable' || type === 'bar' ? { periods: 'hourly' as const } : {}), ...(type === 'image' ? { fit: 'contain' as const } : {}), ...(type === 'xy' ? { colorByTime: true } : {}) } };
}

const mapPage = (doc: DashboardDoc, pageId: string, fn: (page: DashboardPage) => DashboardPage): DashboardDoc => ({ ...doc, pages: doc.pages.map((p) => (p.id === pageId ? fn(p) : p)) });

export function addWidget(doc: DashboardDoc, pageId: string, type: WidgetType): { doc: DashboardDoc; widgetId: string } {
  let widgetId = '';
  const next = mapPage(doc, pageId, (page) => {
    widgetId = newId('w', Object.keys(page.widgets));
    const size = DEFAULT_SIZE[type];
    const bottom = page.layout.lg.reduce((y, it) => Math.max(y, it.y + it.h), 0);
    // Next to the last widget when the row has room, else on a new row.
    const last = page.layout.lg[page.layout.lg.length - 1];
    const beside = last && last.x + last.w + size.w <= 12 && !page.layout.lg.some((it) => it !== last && it.y + it.h > last.y && it.x >= last.x + last.w);
    const at = beside ? { x: last.x + last.w, y: last.y } : { x: 0, y: bottom };
    return { ...page, layout: { ...page.layout, lg: [...page.layout.lg, { i: widgetId, ...at, ...size }] }, widgets: { ...page.widgets, [widgetId]: newWidget(type) } };
  });
  return { doc: next, widgetId };
}

export const updateWidget = (doc: DashboardDoc, pageId: string, widgetId: string, widget: Widget): DashboardDoc =>
  mapPage(doc, pageId, (page) => ({ ...page, widgets: { ...page.widgets, [widgetId]: widget } }));

export const removeWidget = (doc: DashboardDoc, pageId: string, widgetId: string): DashboardDoc =>
  mapPage(doc, pageId, (page) => {
    const widgets = { ...page.widgets };
    delete widgets[widgetId];
    return { ...page, layout: { ...page.layout, lg: page.layout.lg.filter((it) => it.i !== widgetId) }, widgets };
  });

export function duplicateWidget(doc: DashboardDoc, pageId: string, widgetId: string): DashboardDoc {
  return mapPage(doc, pageId, (page) => {
    const source = page.widgets[widgetId];
    const at = page.layout.lg.find((it) => it.i === widgetId);
    if (!source || !at) return page;
    const id = newId('w', Object.keys(page.widgets));
    const bottom = page.layout.lg.reduce((y, it) => Math.max(y, it.y + it.h), 0);
    return { ...page, layout: { ...page.layout, lg: [...page.layout.lg, { ...at, i: id, y: bottom }] }, widgets: { ...page.widgets, [id]: structuredClone(source) } };
  });
}

/** Adds a copied widget (possibly from another dashboard) at the bottom of the page. */
export function pasteWidget(doc: DashboardDoc, pageId: string, widget: Widget, size?: { w: number; h: number }): { doc: DashboardDoc; widgetId: string } {
  let widgetId = '';
  const next = mapPage(doc, pageId, (page) => {
    widgetId = newId('w', Object.keys(page.widgets));
    const s = size ?? DEFAULT_SIZE[widget.type];
    const bottom = page.layout.lg.reduce((y, it) => Math.max(y, it.y + it.h), 0);
    return { ...page, layout: { ...page.layout, lg: [...page.layout.lg, { i: widgetId, x: 0, y: bottom, w: Math.min(12, s.w), h: s.h }] }, widgets: { ...page.widgets, [widgetId]: structuredClone(widget) } };
  });
  return { doc: next, widgetId };
}

/** Applies positions coming back from the grid; returns the same doc when nothing moved. */
export function applyLayout(doc: DashboardDoc, pageId: string, layout: ReadonlyArray<{ i: string; x: number; y: number; w: number; h: number }>): DashboardDoc {
  const page = doc.pages.find((p) => p.id === pageId);
  if (!page) return doc;
  const lg: LayoutItem[] = layout.filter((it) => page.widgets[it.i]).map((it) => ({ i: it.i, x: it.x, y: it.y, w: it.w, h: it.h }));
  const same = lg.length === page.layout.lg.length && lg.every((a) => page.layout.lg.some((b) => a.i === b.i && a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h));
  return same ? doc : mapPage(doc, pageId, (p) => ({ ...p, layout: { ...p.layout, lg } }));
}

export function addPage(doc: DashboardDoc, name: string): { doc: DashboardDoc; pageId: string } {
  const pageId = newId('p', doc.pages.map((p) => p.id));
  return { doc: { ...doc, pages: [...doc.pages, { id: pageId, name, layout: { lg: [], md: [], sm: [] }, widgets: {} }] }, pageId };
}
export const renamePage = (doc: DashboardDoc, pageId: string, name: string): DashboardDoc => mapPage(doc, pageId, (p) => ({ ...p, name }));
export const removePage = (doc: DashboardDoc, pageId: string): DashboardDoc => (doc.pages.length > 1 ? { ...doc, pages: doc.pages.filter((p) => p.id !== pageId) } : doc);

/** Single-column layout for narrow screens: widgets stacked in reading order. */
export function stackedLayout(lg: LayoutItem[]): LayoutItem[] {
  let y = 0;
  return [...lg]
    .sort((a, b) => a.y - b.y || a.x - b.x)
    .map((it) => {
      const item = { i: it.i, x: 0, y, w: 1, h: it.h };
      y += it.h;
      return item;
    });
}

/**
 * Widgets a viewer cannot see any data of, for view mode: every series resolves to no signal
 * for them (out of their access, or deleted) or is refused. Widgets without series (text, image,
 * map…) are never hidden, nor is anything while the bindings are still resolving.
 */
export function emptyWidgets(page: DashboardPage, stateOf: (widgetId: string, refId: string) => string): Set<string> {
  const out = new Set<string>();
  for (const [id, widget] of Object.entries(page.widgets)) {
    if (!isSupported(widget.type) || loadsOwnData(widget.type) || widget.config.series.length === 0) continue;
    const states = widget.config.series.map((s) => stateOf(id, s.refId));
    if (states.every((st) => st === 'unbound' || st === 'denied')) out.add(id);
  }
  return out;
}

/**
 * The layout without the hidden widgets, so nothing looks missing: the space of a hidden widget
 * goes to its neighbours on the same line (left and right share it), as long as that space is
 * free over a neighbour's whole height. Rows left empty close up through the grid's vertical
 * compaction.
 */
export function layoutWithout(lg: LayoutItem[], hidden: Set<string>): LayoutItem[] {
  if (hidden.size === 0) return lg;
  const kept = lg.filter((it) => !hidden.has(it.i)).map((it) => ({ ...it }));
  // Hidden widgets side by side on the same line make one gap.
  const gone: LayoutItem[] = [];
  for (const it of lg.filter((x) => hidden.has(x.i)).sort((a, b) => a.y - b.y || a.x - b.x)) {
    const prev = gone[gone.length - 1];
    if (prev && prev.y === it.y && prev.x + prev.w === it.x) prev.w += it.w;
    else gone.push({ ...it });
  }
  const free = (x0: number, x1: number, self: LayoutItem) =>
    x1 > x0 && !kept.some((o) => o !== self && o.x < x1 && o.x + o.w > x0 && o.y < self.y + self.h && o.y + o.h > self.y);
  for (const g of gone) {
    const left = kept.find((o) => o.y === g.y && o.x + o.w === g.x);
    const right = kept.find((o) => o.y === g.y && o.x === g.x + g.w);
    const mid = g.x + Math.floor(g.w / 2);
    const leftAll = !!left && free(g.x, g.x + g.w, left);
    const rightAll = !!right && free(g.x, g.x + g.w, right);
    if (left && right && free(g.x, mid, left) && free(mid, g.x + g.w, right)) {
      left.w += mid - g.x;
      right.w += right.x - mid;
      right.x = mid;
    } else if (left && leftAll) left.w += g.w;
    else if (right && rightAll) {
      right.w += right.x - g.x;
      right.x = g.x;
    }
  }
  return kept;
}
